import React from "react";
import { Line } from "react-chartjs-2";
import { useGlobalSettings } from "../context/GlobalSettingsContext";
import { useCurrencyFormatter } from "../hooks/useCurrencyFormatter";
import MortgageSimulator from "../simulator";

export default function SimulationChartMini({
  sim,
}: {
  sim: MortgageSimulator;
}) {
  const { settings } = useGlobalSettings();
  // Map currency code to symbol
  const currencySymbols: Record<string, string> = {
    GBP: "£",
    USD: "$",
    EUR: "€",
  };
  const currencySymbol =
    currencySymbols[settings.currency] || settings.currency;

  const c = useCurrencyFormatter();

  const ledger = sim.mortgage.ledger.entries;
  const requiredMonths = (sim.requiredMonths ?? ledger.length) + 1;
  const labels = Array.from({ length: requiredMonths }, (_, i) => i + 1);

  // Mortgage balance, padded with zeros if needed
  const mortgageBalance = [
    ...ledger.map((e: any) => e.principal),
    ...Array(Math.max(0, requiredMonths - ledger.length)).fill(0),
  ];

  // Total savings per month (sum over all savings accounts)
  // Prepend null to shift savings by one index (align end-of-month savings with start-of-month mortgage)
  const savingsPerMonth: (number | null)[] = [
    null,
    ...Array(requiredMonths - 1).fill(0),
  ];
  Object.values(sim.savingsTransactions).forEach((transactions: any) => {
    transactions.forEach((tx: any, i: number) => {
      savingsPerMonth[i + 1] =
        (savingsPerMonth[i + 1] as number) + tx.balanceAfter;
    });
  });

  // Check if there are any savings
  const hasSavings = savingsPerMonth.some((v) => v !== null && v > 0);

  // Calculate appropriate year interval for x-axis
  const totalYears = Math.ceil(requiredMonths / 12);
  let yearStep = 1;
  if (totalYears > 65) {
    yearStep = 10;
  } else if (totalYears > 50) {
    yearStep = 7;
  } else if (totalYears > 20) {
    yearStep = 5;
  } else if (totalYears > 5) {
    yearStep = 2;
  }

  // Y-axis: show only a few major currency values
  const maxY = Math.max(
    ...mortgageBalance,
    ...(hasSavings
      ? savingsPerMonth.filter((v): v is number => v !== null)
      : [0]),
  );
  const yStep = maxY > 200000 ? 50000 : 20000;
  const yTicks = [];
  for (let v = 0; v <= maxY; v += yStep) {
    yTicks.push(v);
  }

  const data = {
    labels: labels,
    datasets: [
      {
        label: "Mortgage Balance",
        data: mortgageBalance,
        pointRadius: 0,
        fill: false,
        tension: 0.1,
      },
      ...(hasSavings
        ? [
            {
              label: "Savings & Investments",
              data: savingsPerMonth,
              pointRadius: 0,
              fill: false,
              tension: 0.1,
            },
          ]
        : []),
    ],
  };

  const options = {
    responsive: true,
    layout: {
      padding: {
        right: 15,
      },
    },
    animation: {
      duration: 0,
    },
    interaction: {
      intersect: false,
      mode: "index" as const,
    },
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
      tooltip: {
        position: "nearest" as const,
        yAlign: "bottom" as const,
        xAlign: "center" as const,
        callbacks: {
          title: function (tooltip: any) {
            let date;
            let year = Math.floor(tooltip[0].parsed.x / 12);
            let month = tooltip[0].parsed.x % 12;
            if (year > 0) {
              date = `${year} year${year === 1 ? "" : "s"} and ${month} month${month === 1 ? "" : "s"} elapsed`;
            } else {
              date = `${month} month${month === 1 ? "" : "s"} elapsed`;
            }
            return date;
          },
          label: function (context: any) {
            let label = context.dataset.label || "";

            if (label) {
              label += ": ";
            }

            if (context.parsed.y) {
              label += c(context.parsed.y);
            } else {
              label += `${currencySymbol}0`;
            }
            return label;
          },
          footer: function (tooltip: any) {
            const mortgage =
              tooltip.find((t: any) => t.dataset.label === "Mortgage Balance")
                ?.parsed.y || 0;
            const savings =
              tooltip.find(
                (t: any) => t.dataset.label === "Savings & Investments",
              )?.parsed.y || 0;
            const net = mortgage - savings;
            return `Net: ${c(net)}`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function (value: any) {
            if (value === 0) return `${currencySymbol}0`;
            if (value >= 1000)
              return `${currencySymbol}${(value / 1000).toFixed(0)}k`;
            return `${currencySymbol}${value}`;
          },
          values: yTicks,
          font: { size: 12 },
          maxTicksLimit: 5,
        },
        grid: { display: false },
      },
      x: {
        ticks: {
          font: { size: 12 },
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
          callback: function (value: any) {
            const monthIndex = Number(value);
            // Only show ticks at whole years (month 0)
            if (monthIndex % 12 === 0) {
              const year = Math.floor(monthIndex / 12) + 1;
              // Only show at yearStep intervals
              if ((year - 1) % yearStep === 0 || year === 1) {
                return `Y${year}`;
              }
            }
            return "";
          },
        },
        grid: { display: false },
      },
    },
  };

  return <Line data={data} options={options} height={120} />;
}
